// RiskHeatmapDashboard.jsx
import React, { useCallback, useEffect, useState, useRef } from "react";
import { getRiskCategories, getRiskHeatmap } from "./riskService";
import { useAuth } from "./useAuth";
import RiskDetailDrawer from "./RiskDetailDrawer";
import Button from "./ui/Button";
import { DonutChart } from "./components/dashboard/SummaryCharts";
import { riskBandSeries, validateHeatmap, chartScopeKey } from "./components/dashboard/chartData";
import { canViewRisks } from "./accessControl";
import { typography } from "./ui/tokens";
import { bandToColor, getCellScore, scoreToBand } from "./utils/riskMatrixLogic";

export default function RiskHeatmapDashboard() {
  const { token, user, accessReady } = useAuth();
  if (!accessReady) return <p role="status">Loading your access...</p>;
  if (!token || !canViewRisks(user)) return <p>Risk data is unavailable with your current access.</p>;
  return <RiskHeatmapContent key={chartScopeKey(user, token)} />;
}

function RiskHeatmapContent() {
  const { token } = useAuth();
  const request = useRef(0);
  useEffect(() => () => { request.current += 1; }, []);
  const [heatmap, setHeatmap] = useState(null);
  const [heatmapCells, setHeatmapCells] = useState(null);
  const [matrixConfig, setMatrixConfig] = useState(null);
  const [heatmapView, setHeatmapView] = useState("residual");
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null); // {impact, likelihood}
  const [cellRisks, setCellRisks] = useState([]);
  const [drawerRiskId, setDrawerRiskId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadHeatmap = useCallback(() => {
    if (!token) return Promise.resolve();
    const requestId = ++request.current;
    setLoading(true);
    setError(null);
    return getRiskHeatmap(heatmapView)
      .then((res) => {
        if (requestId !== request.current) return;
        const payload = validateHeatmap(res?.data ?? res);
        setHeatmap(payload.grid);
        setHeatmapCells(payload.cells || null);
        setMatrixConfig(payload.matrix_config || null);
      })
      .catch(() => { if (requestId === request.current) setError("Unable to load risk heatmap."); })
      .finally(() => { if (requestId === request.current) setLoading(false); });
  }, [token, heatmapView]);

  useEffect(() => {
    loadHeatmap();
  }, [loadHeatmap]);

  useEffect(() => {
    const handleMatrixConfigChanged = () => {
      loadHeatmap();
    };
    window.addEventListener("riskMatrixConfigChanged", handleMatrixConfigChanged);
    return () => window.removeEventListener("riskMatrixConfigChanged", handleMatrixConfigChanged);
  }, [loadHeatmap]);

  useEffect(() => {
    if (!token) return;
    setCategoriesLoading(true);
    setCategoriesError(null);
    getRiskCategories({ include_archived: false })
      .then((res) => {
        const items = res?.data || res || [];
        const names = Array.from(new Set(items.map((c) => c?.name).filter(Boolean)));
        setCategories(names);
      })
      .catch((err) => {
        setCategories([]);
        setCategoriesError(err?.response?.data?.error || err?.message || "Unable to load categories.");
      })
      .finally(() => setCategoriesLoading(false));
  }, [token]);

  useEffect(() => {
    setSelectedCell(null);
    setCellRisks([]);
  }, [heatmapView]);

  const filterByCategory = useCallback((risks) => {
    if (!selectedCategories.length) return risks;
    return risks.filter((r) => r?.category && selectedCategories.includes(r.category));
  }, [selectedCategories]);

  const handleCellClick = (impact, likelihood) => {
    if (!heatmap) return;
    const risks = filterByCategory(heatmap?.[impact]?.[likelihood] || []);
    setSelectedCell({ impact, likelihood });
    setCellRisks(risks);
  };

  useEffect(() => {
    if (!selectedCell || !heatmap) return;
    const risks = filterByCategory(heatmap?.[selectedCell.impact]?.[selectedCell.likelihood] || []);
    setCellRisks(risks);
  }, [filterByCategory, heatmap, selectedCell]);

  const handleRiskClick = (riskId) => {
    setDrawerRiskId(riskId);
  };

  const handleRiskUpdated = useCallback(() => {
    loadHeatmap();
  }, [loadHeatmap]);

  const handleAddRisk = () => {
    window.dispatchEvent(
      new CustomEvent("navigateModule", {
        detail: { module: "risks", action: "openAddRisk" },
      })
    );
  };

  const handleEditRisks = () => {
    window.dispatchEvent(new CustomEvent("navigateModule", { detail: "risks" }));
  };

  const renderEmptyHeatmap = () => (
    <div>
      <h2 style={{ marginBottom: 8, fontSize: typography.size.xl }}>Risk Heatmap</h2>
      <p style={{ marginTop: 0, marginBottom: 12, color: "#6b7280", fontSize: typography.size.base }}>
        The heatmap appears once risks have likelihood and impact set.
      </p>
      <div
        style={{
          borderRadius: 12,
          border: "1px dashed #d1d5db",
          padding: 16,
          background: "#f9fafb",
          display: "grid",
          gap: 10,
          maxWidth: 520,
        }}
      >
        <div style={{ fontWeight: typography.weight.medium, color: "#111827" }}>No heatmap data yet</div>
        <div style={{ fontSize: typography.size.sm, color: "#6b7280" }}>
          Add risks and record likelihood and impact to populate this view automatically.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button variant="primary" onClick={handleAddRisk}>
            Add a risk
          </Button>
          <Button variant="secondary" onClick={handleEditRisks}>
            Edit risks
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <p>Loading risk heatmap...</p>;
  }

  if (error) {
    return <div><p role="alert">{error}</p><Button variant="secondary" onClick={loadHeatmap}>Retry</Button></div>;
  }

  if (!heatmap) {
    return renderEmptyHeatmap();
  }

  const matrixSize = Number(matrixConfig?.matrix_size) || 5;
  const likelihoods = Array.from({ length: matrixSize }, (_, index) => index + 1);
  const impacts = [...likelihoods].reverse();

  const scoreLabel = heatmapView === "inherent" ? "Inherent" : "Residual";

  const getCellColor = (count, impact, likelihood) => {
    if (count === 0) return "#f3f4f6";
    const matrixCellColor = heatmapCells?.[impact]?.[likelihood]?.color;
    if (matrixCellColor) return matrixCellColor;
    const score = getCellScore({
      scoringMode: matrixConfig?.scoring_mode,
      cellScores: matrixConfig?.cell_scores,
      lik: likelihood,
      imp: impact,
    });
    const band = scoreToBand({ score, bandThresholds: matrixConfig?.band_thresholds });
    return bandToColor({ band, bandColors: matrixConfig?.band_colors });
  };

  return (
    <div>
      <h2 style={{ marginBottom: 8, fontSize: typography.size.xl }}>Risk Heatmap</h2>
      <p style={{ marginTop: 0, marginBottom: 16, color: "#6b7280", fontSize: typography.size.base }}>
        Likelihood vs Impact view of all assessed risks. Click a cell to view the risks in that bucket.
      </p>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: typography.size.xs, color: "#6b7280" }}>Score view:</span>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: typography.size.sm }}>
          <input
            type="radio"
            name="heatmap-view"
            value="residual"
            checked={heatmapView === "residual"}
            onChange={() => setHeatmapView("residual")}
          />
          Residual
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: typography.size.sm }}>
          <input
            type="radio"
            name="heatmap-view"
            value="inherent"
            checked={heatmapView === "inherent"}
            onChange={() => setHeatmapView("inherent")}
          />
          Inherent
        </label>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <span style={{ fontSize: typography.size.xs, color: "#6b7280" }}>Categories:</span>
        {categoriesLoading && <span style={{ fontSize: typography.size.xs, color: "#6b7280" }}>Loading...</span>}
        {!categoriesLoading && categories.length === 0 && (
          <span style={{ fontSize: typography.size.xs, color: "#6b7280" }}>All categories</span>
        )}
        {!categoriesLoading &&
          categories.map((c) => (
            <label key={c} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: typography.size.sm }}>
              <input
                type="checkbox"
                checked={selectedCategories.includes(c)}
                onChange={() =>
                  setSelectedCategories((prev) =>
                    prev.includes(c) ? prev.filter((v) => v !== c) : [...prev, c]
                  )
                }
              />
              {c}
            </label>
          ))}
        {selectedCategories.length > 0 && (
          <Button variant="secondary" onClick={() => setSelectedCategories([])} style={{ padding: "4px 8px" }}>
            Clear
          </Button>
        )}
        {categoriesError && <span style={{ fontSize: typography.size.xs, color: "#b91c1c" }}>{categoriesError}</span>}
      </div>

      <div className="glyder-heatmap-layout">
        {/* HEATMAP GRID */}
        <div className="glyder-heatmap-grid">
          {/* Likelihood labels */}
          <div style={{ marginLeft: 40, marginBottom: 4, display: "flex", gap: 4 }}>
            {likelihoods.map((l) => (
              <div key={l} style={{ width: 60, flexShrink: 0, textAlign: "center", fontSize: typography.size.xs }}>
                L {l}
              </div>
            ))}
          </div>

          {/* Grid */}
          {impacts.map((impact) => (
            <div
              key={impact}
              style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}
            >
              {/* Impact label */}
              <div style={{ width: 40, flexShrink: 0, textAlign: "right", fontSize: typography.size.xs }}>I {impact}</div>

              {/* Cells */}
              {likelihoods.map((likelihood) => {
                const risks = filterByCategory(heatmap?.[impact]?.[likelihood] || []);
                const count = risks.length;
                const isSelected =
                  selectedCell &&
                  selectedCell.impact === impact &&
                  selectedCell.likelihood === likelihood;

                return (
                  <div
                    key={likelihood}
                    onClick={() => handleCellClick(impact, likelihood)}
                    style={{
                      width: 60, flexShrink: 0,
                      height: 50,
                      borderRadius: 8,
                      background: getCellColor(count, impact, likelihood),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: count > 0 ? "pointer" : "default",
                      boxShadow: isSelected
                        ? "0 0 0 2px #111827 inset"
                        : "0 0 0 1px rgba(148,163,184,0.6)",
                      fontSize: typography.size.base,
                      fontWeight: typography.weight.medium,
                    }}
                  >
                    {count || ""}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Axes labels */}
          <div style={{ marginTop: 8, marginLeft: 40, fontSize: typography.size.xs, color: "#6b7280" }}>
            Impact ↑ · Likelihood →
          </div>
        </div>

        <section className="glyder-chart-card glyder-heatmap-distribution" aria-label={`${scoreLabel} risk distribution`}>
          <h3>{scoreLabel} risk distribution</h3>
          <p className="glyder-chart-note">Assessed risks matching the heatmap categories. Unscored risks are excluded. The heatmap source considers up to 10,000 risks.</p>
          <DonutChart label={`${scoreLabel} risk distribution`} centreLabel="assessed risks"
            series={riskBandSeries(heatmap, heatmapCells, matrixConfig, selectedCategories)}
            emptyMessage="No assessed risks match these categories." />
        </section>

        {/* CELL DETAILS PANEL */}
        <div
          className="glyder-heatmap-details"
          style={{
            flex: "1 1 100%",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            padding: 14,
            minHeight: 220,
            background: "#ffffff",
            boxShadow: "0 6px 16px rgba(15,23,42,0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
              alignItems: "center",
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: typography.size.lg }}>Cell Details</h3>
              <p style={{ margin: 0, fontSize: typography.size.xs, color: "#6b7280" }}>
                {selectedCell
                  ? `Impact ${selectedCell.impact} x Likelihood ${selectedCell.likelihood}`
                  : "Select a heatmap cell to view risks."}
              </p>
            </div>
          </div>

          {(!selectedCell || cellRisks.length === 0) && (
            <p style={{ fontSize: typography.size.sm, color: "#9ca3af" }}>
              {selectedCell ? "No risks match the selected categories." : "No risks selected. Click a coloured heatmap cell to view its risks."}
            </p>
          )}

          {selectedCell && cellRisks.length > 0 && (
            <div style={{ display: "grid", gap: 6 }}>
              {cellRisks.map((r) => (
                <div
                  key={r.id}
                  onClick={() => handleRiskClick(r.id)}
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #e5e7eb",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: typography.weight.medium }}>{r.name}</div>
                    <div style={{ fontSize: typography.size.xs, color: "#6b7280" }}>
                      Status: {r.status || "N/A"} | {scoreLabel}: {heatmapView === "inherent"
                        ? (r.inherent_score ?? r.inherent_risk ?? r.score ?? "N/A")
                        : (r.residual_score ?? r.residual_risk ?? r.score ?? "N/A")}
                    </div>
                  </div>
                  <div style={{ fontSize: typography.size.lg }}>></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Drawer for risk details */}
      <RiskDetailDrawer
        riskId={drawerRiskId}
        onRiskUpdated={handleRiskUpdated}
        onClose={() => setDrawerRiskId(null)}
      />
    </div>
  );
}

